import {useEffect} from 'react'

import {flatMap, noop, omit} from 'es-toolkit'
import {isEmpty} from 'es-toolkit/compat'
import {useFormik} from 'formik'

import {useAppDispatch} from '../../../hooks/redux'
import {fatalError} from '../../../redux/features/core'
import {closeModal, reopenAddProviderInfoProductionTimesModal} from '../../../redux/features/modal'

import useAsync from '../../../../../commons/src/hooks/use-async'
import useKeyPress from '../../../../../commons/src/hooks/use-key-press'
import preventDoubleClick from '../../../../../commons/src/services/prevent-double-click'
import * as optionApi from '../../../services/api/option'
import * as providerInfoApi from '../../../services/api/provider-info'
import * as providerInfoOptionApi from '../../../services/api/provider-info-option'
import {toTouchedFormData} from '../../utils/form'

import {ModalMeta, ProviderId, ProviderInfo} from '../../../types'

import Button from '../../../../../commons/src/components/button'
import Headline from '../../../../../commons/src/components/headline'
import Modal from '../../../../../commons/src/components/modal'

import ProductionTimesFormPartial, {
  SingleFormData,
  createValidationSchemaWithProductionTimes,
  formDataToPartialRequest,
  formatProductionTimeName,
  initialValues,
  sortProductionTimes,
  validateProductionTimeOption,
} from '../../provider-info/production-times-form-partial'

type Props = {
  providerId: ProviderId
  providerInfos: ProviderInfo[]
  meta: ModalMeta
  onDone?: () => void
  prefilledInitialValues?: {
    productionTimeFast: string
    productionTimeSlow: string
    productionTimeIds: string[]
  }
}

const AddProviderInfoProductionTimesModal = ({
  meta,
  onDone = noop,
  providerId,
  providerInfos,
  prefilledInitialValues,
}: Props) => {
  const dispatch = useAppDispatch()

  const {
    isLoading: isProductionTimesLoading,
    data: productionTimes = [],
    refresh: refreshProductionTimes,
  } = useAsync(
    () => optionApi.getProductionTimeOptionsWithLinkedProviderInfosByProvider(providerId),
    [],
    {
      onError: error => dispatch(fatalError({error})),
    }
  )

  const formik = useFormik<SingleFormData>({
    initialValues,
    validationSchema: createValidationSchemaWithProductionTimes(productionTimes),
    onSubmit: async (values, actions) => {
      const touchedValues = toTouchedFormData(values, initialValues)
      const providerInfoIds = providerInfos.map(info => info.id)

      try {
        // create production time option linking
        if (touchedValues.productionTimeIds) {
          const linkPromises = flatMap(touchedValues.productionTimeIds, optionId =>
            providerInfoIds.map(providerInfoId =>
              providerInfoOptionApi.create({providerInfoId, optionId})
            )
          )
          await Promise.all(linkPromises)
          refreshProductionTimes()
        }

        // update providerInfo with default production times
        if (touchedValues.productionTimeFast || touchedValues.productionTimeSlow) {
          const providerInfoRequest = formDataToPartialRequest(
            omit(touchedValues, ['productionTimeIds'])
          )

          await providerInfoApi.patchMultiple(providerInfoRequest, providerInfoIds)

          onDone()
          dispatch(closeModal())
        }
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  useEffect(() => {
    if (prefilledInitialValues) {
      formik.setValues(prefilledInitialValues)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledInitialValues])

  const productionTimeOptions = sortProductionTimes(productionTimes).map(productionTime => {
    const isValid = validateProductionTimeOption(productionTime, formik.values)
    return {
      label: formatProductionTimeName(productionTime, !isValid),
      value: productionTime.id,
      disabled: !isValid,
    }
  })

  useKeyPress(
    preventDoubleClick(() => formik.handleSubmit()),
    'Enter'
  )

  return (
    <Modal
      fullscreenMobile={meta.fullscreenMobile}
      headline={<Headline label="Add production time" size="l" />}
      onClose={() => dispatch(closeModal())}
      buttons={
        <>
          <Button hierarchy="ghost" onClick={() => dispatch(closeModal())}>
            Cancel
          </Button>
          <Button
            disabled={formik.isSubmitting || (formik.submitCount > 0 && !isEmpty(formik.errors))}
            onClick={preventDoubleClick(() => formik.handleSubmit())}
          >
            Save
          </Button>
        </>
      }
    >
      <ProductionTimesFormPartial
        formik={formik}
        onRefresh={refreshProductionTimes}
        isLoading={isProductionTimesLoading}
        options={productionTimeOptions}
        reopenPreviousModal={(productionTimeId: string) => {
          dispatch(
            reopenAddProviderInfoProductionTimesModal({
              providerId,
              providerInfos,
              prefilledInitialValues: {
                ...formik.values,
                productionTimeIds: [...formik.values.productionTimeIds, productionTimeId],
              },
            })
          )
        }}
        providerInfos={providerInfos}
      />
    </Modal>
  )
}

export default AddProviderInfoProductionTimesModal
